// src/components/UserDashboard/BillModal.jsx
import React, { useState, useEffect, forwardRef } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getFirestore, collection, query, where, getDocs, doc, writeBatch, serverTimestamp, setDoc } from "firebase/firestore";
import { getApp } from "firebase/app";
import { MdClose } from "react-icons/md";
import { useRef } from "react";
import { runTransaction } from "firebase/firestore";



const PrintableBill = ({ businessName, gstNumber, businessAddress, items, group, serviceCharge, getSubtotal, getTaxTotal, getTotal, getFinalPrice, paymentMode }) => {
  return (
    <div
      className="print-area p-6 text-sm text-gray-900 bg-white"
      style={{ WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}
    >


      {/* Header */}
      <div className="mb-4 border-b pb-2">
        <h1 className="text-xl font-bold">{businessName}</h1>

        {businessAddress && (
          <p className="text-xs text-gray-600 leading-snug">
            {businessAddress}
          </p>
        )}

        {gstNumber && (
          <p className="text-xs mt-1">GSTIN: {gstNumber}</p>
        )}
      </div>


      {/* Customer */}
      <div className="mb-4">
        <p><strong>Customer:</strong> {group.customerName}</p>
        <p><strong>Phone:</strong> {group.phone}</p>
        {group.roomNumber && <p><strong>Room:</strong> {group.roomNumber}</p>}
        {group.tableNumber && <p><strong>Table:</strong> {group.tableNumber}</p>}
      </div>

      {/* Items */}
      <table className="w-full border text-xs mb-4">
        <thead className="bg-gray-100">
          <tr>
            <th className="border p-2 text-left">Item</th>
            <th className="border p-2 text-right">Price</th>
            <th className="border p-2 text-right">GST%</th>
            <th className="border p-2 text-right">VAT%</th>
            <th className="border p-2 text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={i}>
              <td className="border p-2">{item.name}</td>
              <td className="border p-2 text-right">₹{item.basePrice}</td>
              <td className="border p-2 text-right">{item.gstPercent || 0}%</td>
              <td className="border p-2 text-right">{item.vatPercent || 0}%</td>
              <td className="border p-2 text-right">
                ₹{getFinalPrice(item).toFixed(2)}
              </td>

            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div className="text-right space-y-1">
        <p>Subtotal: ₹{getSubtotal().toFixed(2)}</p>
        <p>Tax: ₹{getTaxTotal().toFixed(2)}</p>
        <p>Service Charge: ₹{serviceCharge || 0}</p>
        <p className="font-bold text-lg">Total: ₹{getTotal().toFixed(2)}</p>
      </div>
      <div className="mt-4 text-sm">
        <p><strong>Payment Mode:</strong> {paymentMode || "-"}</p>
      </div>
      <p className="mt-6 text-xs text-center italic">
        We’re grateful to serve you
      </p>
    </div>
  );
};

const BillModal = ({ isOpen, onClose, group, groupKey, onBillingSuccess }) => {
  const [items, setItems] = useState([]);
  const [serviceCharge, setServiceCharge] = useState(0);
  const [gstNumber, setGstNumber] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [businessName, setBusinessName] = useState("");
  const ownerEmail = localStorage.getItem("user");
  const [businessAddress, setBusinessAddress] = useState("");
  const [saveForReporting, setSaveForReporting] = useState(false); // default unchecked
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentMode, setPaymentMode] = useState("");


  // 🔹 Fetch businessName & gstNumber
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!ownerEmail) return;
      try {
        const db = getFirestore(getApp());
        const q = query(collection(db, "userDetails"), where("email", "==", ownerEmail));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const data = querySnapshot.docs[0].data();
          setBusinessName(data.businessName || "");
          setGstNumber(data.gstNumber || "");
          setBusinessAddress(data.address || "");
        }
      } catch (err) {
        console.error("Error fetching user details:", err);
      }
    };
    fetchUserDetails();
  }, [ownerEmail]);

  useEffect(() => {
    if (group && group.orders) {
      const allItems = group.orders.flatMap((order) => {
        const orderItems = order.items?.split(",") || [];
        return orderItems.map((itemText) => {
          const nameOnly = itemText.replace(/\s*\(₹\d+(\.\d{1,2})?\)/, "").trim();
          const match = itemText.match(/₹(\d+)/);
          const price = match ? parseFloat(match[1]) : 0;
          return { name: nameOnly, basePrice: price, gstPercent: 5, vatPercent: 0 };
        });
      });
      setItems(allItems);
    }
  }, [group]);


  const handleItemChange = (index, field, value) => {
    const newItems = [...items];

    if (field === "gstPercent") {
      newItems[index].gstPercent = value;
      if (value > 0) newItems[index].vatPercent = 0;
    }

    else if (field === "vatPercent") {
      newItems[index].vatPercent = value;
      if (value > 0) newItems[index].gstPercent = 0;
    }

    else {
      newItems[index][field] = value;
    }

    setItems(newItems);
  };


  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleAddItem = () => {
    setItems([...items, { name: "", basePrice: 0, gstPercent: 5, vatPercent: 0 }]);
  };

  const getFinalPrice = (item) => {
    const gstAmount = (item.basePrice * (item.gstPercent || 0)) / 100;
    const vatAmount = (item.basePrice * (item.vatPercent || 0)) / 100;
    return item.basePrice + gstAmount + vatAmount;
  };


  const getSubtotal = () => items.reduce((sum, i) => sum + i.basePrice, 0);
  const getTaxTotal = () =>
    items.reduce((sum, i) => {
      const gst = (i.basePrice * (i.gstPercent || 0)) / 100;
      const vat = (i.basePrice * (i.vatPercent || 0)) / 100;
      return sum + gst + vat;
    }, 0);
  const getTotal = () => getSubtotal() + getTaxTotal() + parseFloat(serviceCharge || 0);

  const generateThermal80mm = (invoiceNo, isDraft = false) => {

    const baseHeight = 130;
    const itemHeight = items.length * 8;
    const dynamicHeight = baseHeight + itemHeight;

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: [80, dynamicHeight],
    });

    const pageWidth = 80;
    const left = 5;
    const right = 75;
    const center = pageWidth / 2;

    let y = 6;

    pdf.setFont("courier", "normal");
    pdf.setFontSize(8);

    // ================= HEADER =================
    pdf.setFont(undefined, "bold");
    pdf.text(businessName || "", center, y, { align: "center" });
    y += 4;

    pdf.setFont(undefined, "normal");

    if (businessAddress) {
      pdf.text(businessAddress, center, y, { align: "center", maxWidth: 70 });
      y += 4;
    }

    if (gstNumber) {
      pdf.text(`GSTIN: ${gstNumber}`, center, y, { align: "center" });
      y += 4;
    }

    pdf.line(left, y, right, y);
    y += 4;

    // ================= INVOICE INFO =================
    const date = new Date().toLocaleString();

    pdf.text(isDraft ? "DRAFT" : `Invoice: ${invoiceNo}`, left, y);
    y += 4;

    pdf.text(`Date: ${date}`, left, y);
    y += 4;

    pdf.line(left, y, right, y);
    y += 4;

    // ================= CUSTOMER =================
    pdf.text(`Customer: ${group.customerName || "-"}`, left, y);
    y += 4;

    pdf.text(`Phone: ${group.phone || "-"}`, left, y);
    y += 4;

    if (group.tableNumber) {
      pdf.text(`Table: ${group.tableNumber}`, left, y);
      y += 4;
    }

    if (group.roomNumber) {
      pdf.text(`Room: ${group.roomNumber}`, left, y);
      y += 4;
    }

    pdf.line(left, y, right, y);
    y += 4;

    // ================= ITEMS =================
    pdf.setFont(undefined, "bold");
    pdf.text("Item", left, y);
    pdf.text("Total", right, y, { align: "right" });
    y += 4;

    pdf.setFont(undefined, "normal");
    pdf.line(left, y, right, y);
    y += 3;

    items.forEach((item) => {
      const total = getFinalPrice(item).toFixed(2);
      const splitName = pdf.splitTextToSize(item.name, 55);

      splitName.forEach((line) => {
        pdf.text(line, left, y);
        y += 3.5;
      });

      pdf.text(total, right, y - 3.5, { align: "right" });
      y += 2;
    });

    pdf.line(left, y, right, y);
    y += 4;

    // ================= TOTALS =================
    pdf.text("Subtotal", left, y);
    pdf.text(getSubtotal().toFixed(2), right, y, { align: "right" });
    y += 4;

    pdf.text("Tax", left, y);
    pdf.text(getTaxTotal().toFixed(2), right, y, { align: "right" });
    y += 4;

    pdf.text("Service", left, y);
    pdf.text(parseFloat(serviceCharge || 0).toFixed(2), right, y, { align: "right" });
    y += 5;

    pdf.setFont(undefined, "bold");
    pdf.text("TOTAL", left, y);
    pdf.text(getTotal().toFixed(2), right, y, { align: "right" });
    y += 6;

    pdf.setFont(undefined, "normal");

    pdf.line(left, y, right, y);
    y += 5;

    pdf.text(`Payment: ${paymentMode}`, left, y);
    y += 6;

    pdf.text("Thank You! Visit Again!", center, y, { align: "center" });

    pdf.autoPrint();
    window.open(pdf.output("bloburl"), "_blank");
  };

  const generatePDF = (invoiceNo, isDraft = false) => {

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    pdf.setTextColor(0, 0, 0);

    const pageWidth = pdf.internal.pageSize.getWidth();
    const invoiceDateFormatted = new Date().toLocaleString();

    // ================= HEADER =================
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.text(businessName || "", 10, 15);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);

    let y = 20;

    if (businessAddress) {
      pdf.text(businessAddress, 10, y);
      y += 5;
    }

    if (gstNumber) {
      pdf.text(`GSTIN: ${gstNumber}`, 10, y);
      y += 5;
    }

    // RIGHT SIDE - Invoice Info
    pdf.setFont("helvetica", "bold");
    pdf.text(isDraft ? "DRAFT INVOICE" : "Invoice", pageWidth - 10, 15, { align: "right" });

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.text(`Invoice No: ${invoiceNo}`, pageWidth - 10, 22, { align: "right" });
    pdf.text(`Date: ${invoiceDateFormatted}`, pageWidth - 10, 27, { align: "right" });

    // ================= CUSTOMER =================
    y += 10;

    pdf.setFont("helvetica", "bold");
    pdf.text("Customer:", 10, y);

    pdf.setFont("helvetica", "normal");
    pdf.text(group.customerName || "", 30, y);

    y += 5;
    pdf.text("Phone:", 10, y);
    pdf.text(group.phone || "", 30, y);

    if (group.roomNumber !== null && group.roomNumber !== undefined) {
      y += 5;
      pdf.text("Room:", 10, y);
      pdf.text(String(group.roomNumber), 30, y);
    }

    if (group.tableNumber !== null && group.tableNumber !== undefined) {
      y += 5;
      pdf.text("Table:", 10, y);
      pdf.text(String(group.tableNumber), 30, y);
    }

    // ================= ITEMS TABLE =================
    autoTable(pdf, {
      startY: 55,
      theme: "plain",

      head: [["Item", "Price", "GST%", "VAT%", "Total"]],

      body: items.map((item) => [
        item.name,
        `Rs. ${item.basePrice.toFixed(2)}`,
        `${item.gstPercent || 0}%`,
        `${item.vatPercent || 0}%`,
        `Rs. ${getFinalPrice(item).toFixed(2)}`
      ]),

      foot: [
        ["", "", "", "Subtotal", `Rs. ${getSubtotal().toFixed(2)}`],
        ["", "", "", "Tax", `Rs. ${getTaxTotal().toFixed(2)}`],
        ["", "", "", "Service", `Rs. ${parseFloat(serviceCharge || 0).toFixed(2)}`],
        ["", "", "", "TOTAL", `Rs. ${getTotal().toFixed(2)}`],
      ],

      styles: {
        fontSize: 8,
        cellPadding: 2,
        textColor: [0, 0, 0],
        lineColor: [220, 220, 220],
        lineWidth: 0.1,
      },

      headStyles: {
        fillColor: [245, 245, 245],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        lineWidth: 0.2,
      },

      bodyStyles: {
        fillColor: [255, 255, 255],
        lineWidth: 0.1,
      },

      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        lineWidth: 0.2,
      },
    });

    // ================= SIGNATURE SECTION =================
    const finalY = pdf.lastAutoTable.finalY || 55;
    const signatureY = finalY + 20;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);

    pdf.text("Customer Signature:", 10, signatureY);

    // Signature line
    pdf.line(10, signatureY + 8, 80, signatureY + 8);

    pdf.text(`Date: ${invoiceDateFormatted}`, 10, signatureY + 15);
    pdf.text(`Payment Mode: ${paymentMode}`, 10, signatureY + 22);

    // ================= FOOTER =================
    pdf.setFontSize(8);
    pdf.text("We’re grateful to serve you", pageWidth / 2, 285, { align: "center" });

    // ================= PRINT =================
    pdf.autoPrint();
    window.open(pdf.output("bloburl"), "_blank");
  };

  const createInvoice = async () => {

    const db = getFirestore(getApp());
    const remainingOrders = group.orders.filter(order => !order.isBilled);

    // ===============================
    // DRAFT MODE
    // ===============================
    if (!saveForReporting) {
      return {
        invoiceNo: `DRAFT-${Date.now()}`,
        isDraft: true
      };
    }

    // ===============================
    // FULLY BILLED CHECK
    // ===============================
    if (remainingOrders.length === 0) {
      throw new Error("All orders in this group are already billed.");
    }

    let invoiceNo = "";

    await runTransaction(db, async (transaction) => {

      const businessRef = doc(db, "business", ownerEmail);
      const businessSnap = await transaction.get(businessRef);

      let lastInvoiceNumber = 0;

      if (businessSnap.exists()) {
        lastInvoiceNumber = businessSnap.data().lastInvoiceNumber || 0;
      }

      const newInvoiceNumber = lastInvoiceNumber + 1;
      invoiceNo = `INV-${String(newInvoiceNumber).padStart(7, "0")}`;

      const billingRef = doc(
        collection(db, "business", ownerEmail, "billing")
      );

      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth() + 1;
      const datePart = today.toISOString().slice(0, 10).replace(/-/g, "");

      transaction.set(billingRef, {
        invoiceNo,
        invoiceSequence: newInvoiceNumber,
        invoiceDate: serverTimestamp(),
        businessDate: datePart,
        year,
        month,

        email: ownerEmail,
        orderType: group.orderType || "UNKNOWN",
        paymentMode,

        customerName: group.customerName || null,
        customerPhone: group.phone || null,
        tableNumber: group.tableNumber ?? null,
        roomNumber: group.roomNumber ?? null,

        items: items.map(item => ({
          name: item.name,
          basePrice: item.basePrice,
          gstPercent: item.gstPercent,
          vatPercent: item.vatPercent,
          finalPrice: getFinalPrice(item)
        })),

        subtotal: getSubtotal(),
        taxTotal: getTaxTotal(),
        serviceCharge: parseFloat(serviceCharge || 0),
        grandTotal: getTotal(),

        relatedOrderIds: remainingOrders.map(o => o.id),
        createdAt: serverTimestamp()
      });

      transaction.set(
        businessRef,
        { lastInvoiceNumber: newInvoiceNumber },
        { merge: true }
      );

      remainingOrders.forEach(order => {
        const orderRef = doc(db, "orders", order.id);
        transaction.update(orderRef, {
          isBilled: true,
          invoiceNo,
          billedAt: serverTimestamp()
        });
      });

    });

    return {
      invoiceNo,
      isDraft: false
    };
  };

  const handlePrintThermal = async () => {

    if (!paymentMode) {
      alert("Please select a payment mode before generating the bill.");
      return;
    }

    if (isProcessing) return;
    setIsProcessing(true);

    try {

      const { invoiceNo, isDraft } = await createInvoice();

      generateThermal80mm(invoiceNo, isDraft);

      onClose();
      if (onBillingSuccess) {
        onBillingSuccess();
      }

    } catch (error) {
      alert(error.message || "Billing failed.");
    }

    setIsProcessing(false);
  };


  const handlePrintPDF = async () => {

    if (!paymentMode) {
      alert("Please select a payment mode before generating the bill.");
      return;
    }

    if (isProcessing) return;
    setIsProcessing(true);

    try {

      const { invoiceNo, isDraft } = await createInvoice();

      generatePDF(invoiceNo, isDraft);

      onClose();
      if (onBillingSuccess) {
        onBillingSuccess();
      }

    } catch (error) {
      alert(error.message || "Billing failed.");
    }

    setIsProcessing(false);
  };

  if (!isOpen || !group) return null;

  return (
    <div>
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center no-print">

        <div className="bg-white w-full max-w-6xl h-[90vh] rounded-xl shadow-xl p-6 flex flex-col">

          {/* Header */}
          <h2 className="text-lg font-bold mb-4">
            Generate Bill for {groupKey} {businessName && `- ${businessName}`}
          </h2>

          {/* Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1 overflow-hidden">

            {/* LEFT: Bill Controls */}
            <div className="overflow-y-auto pr-2 space-y-4">

              {/* Customer Name */}
              <div>
                <label className="block text-sm font-medium mb-1">Customer Name</label>
                <input
                  type="text"
                  disabled
                  value={group.customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              {/* GST */}
              <div>
                <label className="block text-sm font-medium mb-1">Shopkeeper GST No</label>
                <input
                  type="text"
                  value={gstNumber}
                  onChange={(e) => setGstNumber(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              {/* Items Table */}
              <table className="w-full text-sm border rounded overflow-hidden">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 border">Item</th>
                    <th className="p-2 border w-20">Base ₹</th>
                    <th className="p-2 border w-16">GST%</th>
                    <th className="p-2 border w-16">VAT%</th>
                    <th className="p-2 border w-24">Total ₹</th>
                    <th className="p-2 border w-10">❌</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={idx}>
                      <td className="p-2 border">
                        <input
                          value={item.name}
                          onChange={(e) => handleItemChange(idx, "name", e.target.value)}
                          className="w-full border rounded px-2 py-1"
                        />
                      </td>
                      <td className="p-2 border">
                        <input
                          type="number"
                          value={item.basePrice}
                          onChange={(e) =>
                            handleItemChange(idx, "basePrice", parseFloat(e.target.value))
                          }
                          className="w-full border rounded px-2 py-1"
                        />
                      </td>
                      <td className="p-2 border">
                        <input
                          type="number"
                          value={item.gstPercent}
                          onChange={(e) =>
                            handleItemChange(idx, "gstPercent", parseFloat(e.target.value))
                          }
                          className="w-full border rounded px-2 py-1"
                        />
                      </td>
                      <td className="p-2 border">
                        <input
                          type="number"
                          value={item.vatPercent}
                          disabled={item.gstPercent > 0}
                          onChange={(e) =>
                            handleItemChange(idx, "vatPercent", Number(e.target.value))
                          }
                          className="w-full border rounded px-2 py-1 disabled:bg-gray-100"
                        />
                      </td>
                      <td className="p-2 border font-medium">
                        ₹{getFinalPrice(item).toFixed(2)}
                      </td>
                      <td className="p-2 border text-center">
                        <button
                          onClick={() => handleRemoveItem(idx)}
                          className="text-red-500 font-bold"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <button
                onClick={handleAddItem}
                className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1 rounded text-sm"
              >
                + Add Item
              </button>

              {/* Service Charge */}
              <div className="flex justify-between items-center">
                <label className="font-medium">Service Charge (₹)</label>
                <input
                  type="number"
                  value={serviceCharge}
                  onChange={(e) => setServiceCharge(e.target.value)}
                  className="w-28 border rounded px-2 py-1"
                />
              </div>
              <div className="flex items-center justify-between gap-4">
                <label className="text-sm font-medium whitespace-nowrap">
                  Payment Mode <span className="text-red-500">*</span>
                </label>

                <select
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  className="w-48 border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  required
                >
                  <option value="">Select</option>
                  <option value="CASH">Cash</option>
                  <option value="CARD">Card</option>
                  <option value="UPI">UPI</option>
                  <option value="ONLINE">Online</option>
                </select>
              </div>

              <div className="text-right font-bold text-lg">
                Total: ₹{getTotal().toFixed(2)}
              </div>
            </div>

            {/* RIGHT: Bill Preview */}
            <div className="border rounded-xl bg-gray-50 p-4 overflow-y-auto">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                Bill Preview
              </h3>


              {/* SCREEN PREVIEW */}
              <PrintableBill
                businessName={businessName}
                gstNumber={gstNumber}
                businessAddress={businessAddress}
                items={items}
                group={group}
                serviceCharge={serviceCharge}
                getSubtotal={getSubtotal}
                getTaxTotal={getTaxTotal}
                getTotal={getTotal}
                getFinalPrice={getFinalPrice}
                paymentMode={paymentMode}
              />

            </div>

          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between mt-4 border-t pt-4">
            {/* Save for Reporting */}
            <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 accent-blue-600"
                checked={saveForReporting}
                onChange={(e) => setSaveForReporting(e.target.checked)}
              />
              Save this invoice for GST & reporting
            </label>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handlePrintPDF}
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
              >
                Print A4
              </button>
              <button
                type="button"
                onClick={handlePrintThermal}
                className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700"
              >
                Print 80mm
              </button>
            </div>
          </div>

        </div>
      </div>

    </div>
  );

};

export default BillModal;
